import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import ArrayStore from "devextreme/data/array_store";
import DataSource from "devextreme/data/data_source";
import { Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { FiscalProfileBackend } from "src/app/backend/api/fiscal-profile.backend";
import {
  FiscalProfile,
  FiscalProfileCreate,
  FiscalProfileLight,
  FiscalProfileUpdate
} from "src/app/backend/models/fiscal-profile/fiscal-profile";
import { SectionalCreate } from "src/app/backend/models/sectional/sectional.create";
import { InvoiceFiscalProfileTaxRegime } from "src/app/backend/models/invoice/invoice";
import {
  Regions,
  RegionService
} from "src/app/services/feature-access-control/region.service";
import { UserService } from "src/app/services/user.service";
import { sntTranslate } from "src/app/support/translate";

@Injectable()
export class FiscalProfileService {
  readonly #fiscalProfileBackend = inject(FiscalProfileBackend);
  readonly #regionService = inject(RegionService);
  readonly #userService = inject(UserService);

  readonly #registry = new Map<string, FiscalProfile>();

  warm(profiles: FiscalProfile[]): void {
    this.#registry.clear();

    for (const profile of profiles) {
      this.#registry.set(profile.id, profile);
    }
  }

  upsert(profile: FiscalProfile): void {
    this.#registry.set(profile.id, profile);
  }

  remove(id: string): void {
    this.#registry.delete(id);
  }

  findInRegistry(id: string): FiscalProfile | undefined {
    return this.#registry.get(id);
  }

  listFromRegistry(): FiscalProfile[] {
    return Array.from(this.#registry.values());
  }

  findDefaultInRegistry(): FiscalProfile | undefined {
    return this.listFromRegistry().find(
      (profile: FiscalProfile) => profile.default
    );
  }

  load(id: string): Observable<FiscalProfile> {
    const cached = this.#registry.get(id);

    if (cached) {
      return of(cached);
    }

    return this.#fiscalProfileBackend
      .load(id)
      .pipe(tap((profile: FiscalProfile) => this.upsert(profile)));
  }

  loadLight(id: string): Observable<FiscalProfileLight> {
    return this.load(id).pipe(
      map((profile: FiscalProfile) => ({
        id: profile.id,
        name: profile.name,
        default: profile.default
      }))
    );
  }

  listAll(): Observable<FiscalProfile[]> {
    return of(Array.from(this.#registry.values()));
  }

  listAllLight(): Observable<FiscalProfileLight[]> {
    return this.listAll().pipe(
      map((profiles: FiscalProfile[]) =>
        profiles.map((profile: FiscalProfile) => ({
          id: profile.id,
          name: profile.name,
          default: profile.default
        }))
      )
    );
  }

  listAllOrRewarm(): Observable<FiscalProfile[]> {
    return this.listAll().pipe(
      switchMap((profiles: FiscalProfile[]) => {
        if (
          profiles.length > 0 ||
          !this.#userService.able("read", "fiscal-profile")
        ) {
          return of(profiles);
        }

        return this.rewarm();
      })
    );
  }

  rewarm(): Observable<FiscalProfile[]> {
    return this.#fiscalProfileBackend
      .listAll()
      .pipe(tap((profiles: FiscalProfile[]) => this.warm(profiles)));
  }

  refresh(id: string): Observable<FiscalProfile> {
    return this.#fiscalProfileBackend
      .load(id)
      .pipe(tap((profile: FiscalProfile) => this.upsert(profile)));
  }

  create(request: FiscalProfileCreate): Observable<FiscalProfile> {
    return this.#fiscalProfileBackend
      .create(request)
      .pipe(tap((profile: FiscalProfile) => this.upsert(profile)));
  }

  update(id: string, request: Partial<FiscalProfileUpdate>): Observable<void> {
    return this.#fiscalProfileBackend.update(id, request).pipe(
      switchMap(() => this.refresh(id)),
      map((): void => undefined)
    );
  }

  delete(id: string): Observable<void> {
    return this.#fiscalProfileBackend.delete(id).pipe(
      tap(() => this.remove(id)),
      map((): void => undefined)
    );
  }

  addSectional(
    fiscalProfileId: string,
    payload: SectionalCreate
  ): Observable<void> {
    return this.#fiscalProfileBackend
      .addSectional(fiscalProfileId, payload)
      .pipe(
        switchMap(() => this.refresh(fiscalProfileId)),
        map((): void => undefined)
      );
  }

  updateSectional(
    fiscalProfileId: string,
    sectionalId: string,
    request: SectionalCreate
  ): Observable<void> {
    return this.#fiscalProfileBackend
      .updateSectional(fiscalProfileId, sectionalId, request)
      .pipe(
        switchMap(() => this.refresh(fiscalProfileId)),
        map((): void => undefined)
      );
  }

  removeSectional(
    fiscalProfileId: string,
    sectionalId: string
  ): Observable<void> {
    return this.#fiscalProfileBackend
      .removeSectional(fiscalProfileId, sectionalId)
      .pipe(
        switchMap(() => this.refresh(fiscalProfileId)),
        map((): void => undefined)
      );
  }

  getDevxDataSourceByRegion(): DataSource {
    switch (this.#regionService.get()) {
      case Regions.AE: {
        return new DataSource({
          paginate: false,
          store: new ArrayStore({
            data: [
              {
                display: sntTranslate(marker("Regime ordinario")),
                value: InvoiceFiscalProfileTaxRegime.NORMAL_REGIME
              },
              {
                display: sntTranslate(marker("Regime forfettario")),
                value: InvoiceFiscalProfileTaxRegime.FLAT_TAX_REGIME
              }
            ]
          })
        });
      }
      default: {
        return new DataSource({
          paginate: false,
          store: new ArrayStore({
            data: [
              {
                display: sntTranslate(marker("Regime ordinario")),
                value: InvoiceFiscalProfileTaxRegime.NORMAL_REGIME
              },
              {
                display: sntTranslate(marker("Regime forfettario")),
                value: InvoiceFiscalProfileTaxRegime.FLAT_TAX_REGIME
              },
              {
                display: sntTranslate(marker("Regime dei minimi")),
                value: InvoiceFiscalProfileTaxRegime.FACILITATED_TAX_SYSTEM
              }
            ]
          })
        });
      }
    }
  }
}
